import {
	Autocomplete,
	CircularProgress,
	TextField
} from "@mui/material";
import { SBTranslate } from "i18n";
import { SBGrid, SBSelect, SBTextField, SBTypography } from "modules/Commons";
import { SourcingType } from "modules/InventoryConfig/models/sourcingType";
import { title } from "process";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TipsContext } from "views/ConfigsView/ConfigTips";
import { useSuppliersRequester } from "modules/Suppliers/hooks/useSuppliersRequester";
import { debounce } from "lodash";
import { useMemo } from "react";

export default ({
	onUpdate,
	sourcingValue,
	inventoryFormulas,
	stores,
	fetchPropError,
}: any) => {
	const { t } = useTranslation();
	const tipsContext = useContext(TipsContext);
	const [loading, setLoading] = useState(false);
	const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
	const suppliersRequester = useSuppliersRequester();

	const [sourcingType, setSourcingType] = useState<SourcingType | null>(
		sourcingValue?.sourcingType ?? null
	);
	const [sourcing, setSourcing] = useState<{id: any; label: string | null}>({
		id: sourcingValue?.sourcingID || null,
		label: null,
	});

	const options = [
		{
			text: "notDefined",
			value: SourcingType.NotDefined,
			fieldLabel: "none",
		},
		{
			text: "supplier",
			value: SourcingType.Supplier,
			fieldLabel: "supplier",
			items: supplierOptions,
		},
		{
			text: "distribution",
			value: SourcingType.Distribution,
			fieldLabel: "store",
			items: stores?.map((c: any) => ({
				id: c.id,
				label: c.name + ` (${c.clientID})`,
			})),
		},
	];

	const searchSuppliers = useMemo(
		() =>
			debounce((term: string) => {
				if (!term || term.length < 3) {
					setSupplierOptions([]);
					return;
				}
				setLoading(true);
				suppliersRequester
					.GET(`search?name=${encodeURIComponent(term)}`)
					.then((r: any) => {
						setSupplierOptions(
							(r.data || []).map((c: any) => ({
								id: c.id,
								label: c.name + ` (${c.clientID})`,
							}))
						);
					})
					.finally(() => setLoading(false));
			}, 500),
		[suppliersRequester]
	);

	useEffect(() => {
		if (sourcingType === SourcingType.Distribution) {
			setSourcing((s) => ({
				...s,
				label: stores?.find((c: any) => +c.id === +sourcing.id)?.name,
			}));
		}
	}, [stores]);

	useEffect(() => {
		if (sourcingType === SourcingType.Supplier && sourcing.id && !sourcing.label) {
			setLoading(true);
			suppliersRequester.GET(`${sourcing.id}`)
				.then((r: any) => {
					if (r.data) {
						const mapped = {
							id: r.data.id,
							label: r.data.name + ` (${r.data.clientID})`
						};
						setSupplierOptions([mapped]);
						setSourcing((s) => ({ ...s, label: mapped.label }));
					}
				})
				.finally(() => setLoading(false));
		}
	}, []);

	useEffect(() => {
		onUpdate({
			sourcingID: sourcing?.id,
			sourcingType: sourcingType,
		});
	}, [sourcing, sourcingType]);

	const updateInventoryStatus = (value: number) => {
		if (value === SourcingType.NotDefined)
			setSourcingType(null);
		else
			setSourcingType(value);

		setSourcing({ id: null, label: null });
		tipsContext.showTip(
			"Supply" + options.find(r => r.value == value)?.text.capitalize() ?? ""

		);
	}

	return (
		<SBGrid container mt={2}>
			<SBGrid item xs={12} display="flex">
				<SBTypography
					mb={1}
					fontWeight="500"
					color="gray3.main"
					variant={"body2"}
					textTransform={"uppercase"} >
					{SBTranslate("supplyProductBy")}
				</SBTypography>
			</SBGrid>
			<SBGrid item xs={12}>
				<SBSelect
					fullWidth
					getText={item => t(item.text)}
					getValue={item => item.value}
					onChange={(event) => updateInventoryStatus(Number(event.target.value))}
					value={sourcingType ?? SourcingType.NotDefined}
					items={options}
				/>
			</SBGrid>
			<SBGrid item xs={12} mt={1}>
				{options &&
					options.map(
						(option) =>
							option.value === sourcingType &&
							option.items && (
								<Autocomplete
									key={option.value}
									size="small"
									options={option.items}
									getOptionLabel={(option) =>
										option?.label || ""
									}
									loading={loading}
									noOptionsText={option.value === SourcingType.Supplier ? "Digite ao menos 3 caracteres..." : "Nenhum resultado"}
									isOptionEqualToValue={(option, value) => {
										return +option.id === +value.id;
									}}
									renderInput={(params) => (
										<><SBTypography
											mb={1}
											fontWeight="500"
											color="gray3.main"
											variant={"body2"}
											textTransform={"uppercase"}
										>
											{t(
												option.fieldLabel
											).toUpperCase()}
										</SBTypography>
											<TextField
												{...params}
												placeholder={option.value === SourcingType.Supplier ? "Ex: Fornecedor XYZ..." : ""}
												helperText={fetchPropError(
													"sourcingID"
												)}
												error={!!fetchPropError("sourcingID")}
												InputProps={{
													...params.InputProps
												}}
											/></>
									)}
									onInputChange={(e, value) => {
										if (option.value === SourcingType.Supplier) {
											searchSuppliers(value);
										}
									}}
									value={sourcing}
									onChange={(e, newValue: any) =>
										setSourcing(newValue)
									}
								/>
							)
					)}
			</SBGrid>
		</SBGrid>
	);
};
